import type { SampleBucket } from '../../../View/Component/SampleGrouping/OperationTypes.js'
import type { VisualizationPipeline } from '../../../View/Component/SampleGrouping/RunPipeline.js'
import type { BlastSource } from '../../../Model/Project/BlastSource.js'
import type { SerializableFieldsIndex } from './VisualizationSerializableFields.js'
import type { SinkTypeKey } from '../../../View/Component/ConfigurableVisualization/Sinks/SinkRegistry.js'
import type { Taxas } from '../../../Model/ITaxa.js'
import type { SpinSelection } from '../../../Repository/Visualization/CubeKey.js'

export type SCALINGMODE = 'ABSOLUTE' | 'PERCENTAGE'
export type COUNTMODE = 'READS' | 'UNIQUE'

export type { SpinSelection }

export type SourceId = string
export type SinkId = string
export type SinkPanelId = string

export type GridLayoutItem = {
  i: SinkPanelId // required by react-grid-layout
  x: number
  y: number
  w: number
  h: number
  minW?: number
  minH?: number
  maxW?: number
  maxH?: number
  static?: boolean
}

export type GridLayoutItemInit = Omit<GridLayoutItem, 'x' | 'y'> & Partial<Pick<GridLayoutItem, 'x' | 'y'>>

export type GridLayouts = Record<'lg' | 'md' | 'sm' | 'xs' | 'xxs', GridLayoutItem[]>

/* placeholder type for when we later switch to streaming updates of the UI while samples are loading */
export type RunMode = 'block' | 'progressive' | 'progressive-values'

export interface SourceRunPolicy {
  mode: RunMode
  debounceMs?: number
  minSamples?: number
  pageStride?: number
  finalRunOnComplete?: boolean
}

/* how to handle taxons without any traits, if they should be presented or removed */
export type NO_TRAIT_MODE = 'exclude' | 'include'

export const NO_TRAIT = '(no trait data)'

export interface Trait {
  traitgroup: string
  traitname: string
  consensustaxon: string
  percentage: number
  traitsource: string
  created_at: string
}

export interface TraitState {
  traits: Trait[] | undefined
  loadingState: 'initializing' | 'loading' | 'ready' | 'error'
  taxonTraitMap: Record<string, Trait[]> | undefined
  traitGroups: string[] | undefined
}

export interface NatureKPIState {
  /* GROUP mode: segment the chart by the traits within this group (trait composition). */
  traitGroup: string
  /* SINGLE-TRAIT mode: filter the whole source to OTUs carrying this exact trait, then show the normal
   * taxonomic breakdown. Wins over the group overlay; makes noTraitMode irrelevant. */
  traitName?: string
  noTraitMode: NO_TRAIT_MODE
}

export interface SourceSharedState {
  /* current viewing level in the taxa tree */
  taxonLevel?: 'kingdom' | 'phylum' | 'class' | 'order' | 'family' | 'genus' | 'species'
  /* how data should be scaled - absolute values or percentages/normalized to 100% per stack */
  scalingMode?: SCALINGMODE
  /* how data should be counted - absolute read counts or 1 per unique read type */
  countMode?: COUNTMODE
  /* minimum quality grade of the read to be considered */
  gradeThreshold?: number
  /* the blast db type to be sourced */
  blastType: BlastSource
  /* sub-path in the taxonomy tree to be looked at */
  selectedPath?: string[]
  /* instead of looking at taxon names, look at derived traits */
  natureKPIs?: NatureKPIState
  /* when set, source the cube from spin.* data (SPIN clustering) with these knobs; see SpinSelection */
  spin?: SpinSelection
}

export interface SourceConfig {
  kind: 'pipeline/v1'
  pipeline: VisualizationPipeline
  defaults?: SourceSharedState
  /**
   * Optional explicit sample-id restriction applied to the source's input samples BEFORE bucketing
   * (BlockingSourceEpic). Undefined/empty = every sample in scope. Used by the chat agent to render a chart
   * over a specific slice of samples; inert for the normal workspace, which never sets it.
   */
  sampleIds?: string[]
}

export interface SourceState {
  selectedBucketKeys: string[]
}

export interface SourceSnapshot {
  schema: 'buckets+taxa/v1'
  allBuckets: SampleBucket[]
  selectedBuckets: SampleBucket[]
  meta: { pagesLoaded: number; totalPages?: number; elapsedMs?: number; nSamples?: number }
  metadataIndex: SerializableFieldsIndex
}

export type SourceStatus = 'init' | 'assembling' | 'waiting' | 'streaming' | 'finalizing' | 'error' | 'dataReady'

export function isLoadingState(status: SourceStatus): boolean {
  return status === 'assembling' || status === 'waiting' || status === 'streaming' || status === 'finalizing'
}

// --- Sink config/state
export interface SinkConfig<P = Record<string, unknown>> {
  id: SinkId
  kind: SinkTypeKey
  params: P
  title?: string
}

export interface SinkState {
  ui?: Record<string, unknown>
}

// --- Slice state
export interface VizSourceEntity {
  id: SourceId
  name: string
  config: SourceConfig
  shared: SourceSharedState
  /** Snapshot of `shared` captured when this source's card was PINNED — the "restore to pinned view" target
   *  after the user navigates away (zoom/level/blast/trait/…). In-session only (not persisted with the blob). */
  pinnedShared?: SourceSharedState
  state: SourceState
  status: SourceStatus
  error?: string
  policy: SourceRunPolicy

  // last computed memo key for this source run
  sourceKey: string

  // latest snapshot for transformers
  snapshot: SourceSnapshot | null

  // sinks attached to this source
  sinks: Record<SinkId, { config: SinkConfig; state?: SinkState }>
}

export type SourceAction = {
  sourceId: SourceId
}

export type SinkAction = {
  sinkId: SinkId
}

export function isSourceAction(action: unknown): action is SourceAction {
  return (action as SourceAction).sourceId !== undefined
}

export interface VizContext {
  scopeId: string
  targetTaxonomy?: Taxas
}

export interface RemoteViewSummary {
  id: string
  name: string
  comment?: string | null
  isTemplate: boolean
  isShared: boolean
  schemaVersion: string
  createdAt?: string
  modifiedAt?: string
  lastUsedAt?: string
}

export interface RemoteViewsState {
  items: RemoteViewSummary[]
  loading: boolean
  error?: string
}

export type VizScope = { kind: 'project' | 'order'; id: string }
